import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Store } from '@ngrx/store';

import { BugReportFetchRequest, BugReportUpdateStatusRequest } from '../../../store/bug-report/bug-report.actions';
import type { BugReport } from '../../../store/bug-report/bug-report.models';
import { selectBugReports, selectBugReportsLoading } from '../../../store/bug-report/bug-report.selectors';

@Component({
  selector: 'app-super-admin-bug-reports',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatFormFieldModule, MatInputModule, MatIconModule, MatButtonModule],
  templateUrl: './super-admin-bug-reports.html',
  styleUrl: './super-admin-bug-reports.scss',
})
export class SuperAdminBugReports {
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly bugReports = signal<BugReport[]>([]);
  protected readonly bugReportsLoading = signal(false);
  protected readonly searchTerm = signal('');

  constructor() {
    this.store.dispatch(BugReportFetchRequest());

    this.store.select(selectBugReports)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((reports) => {
        this.bugReports.set(reports ?? []);
      });

    this.store.select(selectBugReportsLoading)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loading) => this.bugReportsLoading.set(!!loading));
  }

  protected updateSearchTerm(value: string): void {
    this.searchTerm.set(value);
  }

  protected filteredBugReports(): BugReport[] {
    const query = this.searchTerm().trim().toLowerCase();
    if (!query) {
      return this.bugReports();
    }

    return this.bugReports().filter((report) =>
      [
        report.title,
        report.description,
        report.organization_id,
        report.user_id,
        report.page_url,
        report.app_version,
      ].some((value) => value?.toLowerCase().includes(query)),
    );
  }

  protected formatDate(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }

    return date.toLocaleString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  protected closeBug(report: BugReport): void {
    if (report.status === 'closed') {
      return;
    }

    this.store.dispatch(BugReportUpdateStatusRequest({ id: report.id, status: 'closed' }));
  }
}
